import HelpIcon from "@mui/icons-material/Help";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    DialogContent,
    DialogContentText,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import BackButton from "common/components/BackButton/BackButton";
import GenericAlert from "common/components/GenericAlert/GenericAlert";
import GenericDialog from "common/components/GenericDialog/GenericDialog";
import ImageSlider from "common/components/ImageSlider/ImageSlider";
import TabPanelComponent from "common/components/TabPanel/TabPanelComponent";
import { ApiService } from "common/services/ApiService";
import { useAuth } from "context/AuthContext";
import { camelizeObject } from "helpers/TextHelpers";
import { UseAlert } from "hooks/UseAlert";
import TabNewResolution from "modules/activity/pages/Activities/components/TabNewResolution";
import TabResolution from "modules/activity/pages/Activities/components/TabResolution";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    DEFAULT_DATA_GRAPHER,
    MAP_GRAPHS,
    QUALIFICATION,
    VISIBILITY,
    VISIBILITY_MAP,
} from "utils/constants";

const API_GRAPHER = {
    dataTable: ApiService.getDataGraphBasic,
    column: ApiService.getDataGraphColumn,
    line: ApiService.getDataGraphColumn,
    pie: ApiService.getDataGraphBasic,
    bubble: ApiService.getDataGraphColumn,
    dispersion: ApiService.getDataGraphColumn,
    map: ApiService.getDataGraphMap,
    social_word_frequency: ApiService.getRedditGraph,
    social_user_comments: ApiService.getRedditGraphUserComments,
    static_network: ApiService.getDataGraph,
    word_cloud: ApiService.getDataWordCloud,
};

const Grapher = () => {
    const { id, idActivity } = useParams();
    const { auth } = useAuth();

    const {
        alertText,
        showAlert,
        setAlertText,
        setShowAlert,
        confirmAction,
        executeAction,
        openDialog,
        setOpenDialog,
        dialogContentText,
        setSeverity,
        severity,
    } = UseAlert();

    const [datasetColumns, setDatasetColumns] = useState([]);
    const [dataSource, setDataSource] = useState("static");
    const [graphListAvailable, setGraphListAvailable] = useState([]);
    const [graphListAvailableResolution, setGraphListAvailableResolution] = useState([]);
    const [studentActivity, setStudentActivity] = useState();
    const [valueTab, setValueTab] = useState(Boolean(idActivity) ? 1 : 0);

    const [openLoader, setOpenLoader] = useState(false);
    const [openCorrectionModal, setOpenCorrectionModal] = useState(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
    const [actualFeedbackOpened, setActualFeedbackOpened] = useState();
    const [correctionStatus, setCorrectionStatus] = useState({
        status: null,
        observation: "",
    });

    const [openTutorialModal, setOpenTutorialModal] = useState(false);

    const [dataGraphResolution, setDataGraphResolution] = useState({
        dataTable: {},
        column: {},
        line: {},
        pie: {},
        bubble: {},
        dispersion: {},
        map: {},
        social_user_comments: {},
        social_word_frequency: {},
        static_network: {},
        word_cloud: {},
    });

    const [includeChipList, setIncludeChipList] = useState([]);
    const [excludeChipList, setExcludeChipList] = useState([]);
    const [showMore, setShowMore] = useState(false);
    const [activity, setActivity] = useState(null);

    const criterionMapList = {
        include: [includeChipList, setIncludeChipList],
        exclude: [excludeChipList, setExcludeChipList],
    };

    const mapValueGraphTab = {
        normal: 0,
        betweenness: 1,
    };

    const [graphSelectedMap, setGraphSelectedMap] = useState(DEFAULT_DATA_GRAPHER);

    const [graphSelectedMapResolution, setGraphSelectedMapResolution] =
        useState(DEFAULT_DATA_GRAPHER);

    const [studentActivityResolutions, setStudentActivityResolutions] = useState([]);
    const [actualResolution, setActualResolution] = useState({});

    const mapGraphSelectedMapTab = {
        0: graphSelectedMapResolution,
        1: graphSelectedMap,
    };

    const setConfigurationResolutions = async (dataGraph) => {
        setOpenLoader(true);
        let dataGraphResolutionAux = { ...dataGraphResolution };
        const dataGraphCamelized = camelizeObject(dataGraph);

        const existCorrection = [
            QUALIFICATION.approved,
            QUALIFICATION.disapproved,
            QUALIFICATION.reDelivery,
        ].includes(dataGraphCamelized?.feedback?.qualification);
        setCorrectionStatus({
            status: existCorrection ? dataGraphCamelized?.feedback?.qualification : null,
            observation: existCorrection ? dataGraphCamelized?.feedback?.observation : "",
        });

        setActualResolution(dataGraphCamelized);
        setActualFeedbackOpened(dataGraphCamelized);

        if (dataGraphCamelized.partialDeliveries.length > 0) {
            const graphs = [];
            const storedData = dataGraphCamelized.partialDeliveries[0].data;

            await Promise.all(
                dataGraphCamelized.partialDeliveries[0].graphs.map(async (graph) => {
                    const projectHeadersMap = {};

                    if (
                        graph.name !== "word_cloud" &&
                        storedData[graph.name]?.columns?.value?.length > 0
                    ) {
                        storedData[graph.name].columns.value.forEach((h) => {
                            projectHeadersMap[h] = 1;
                        });
                    }

                    const includeHeadersMap = {};
                    const excludeHeadersMap = {};

                    let params = {};

                    if (!graph.name.includes("social")) {
                        storedData[graph.name].filterInclude.forEach((obj) => {
                            if (!(obj.column in includeHeadersMap)) {
                                includeHeadersMap[obj.column] = [];
                            }
                            includeHeadersMap[obj.column].push(obj.valueFilter);
                        });
                        storedData[graph.name].filterExclude.forEach((obj) => {
                            if (!(obj.column in excludeHeadersMap)) {
                                excludeHeadersMap[obj.column] = [];
                            }
                            excludeHeadersMap[obj.column].push(obj.valueFilter);
                        });

                        params = {
                            graph: graph.name,
                            project_headers_map: projectHeadersMap,
                            columns: storedData[graph.name].columns,
                            include_headers_map: includeHeadersMap,
                            exclude_headers_map: excludeHeadersMap,
                        };
                    } else {
                        params = {
                            frequency: storedData[graph.name].frequency,
                            word: storedData[graph.name].word,
                            sar_id: dataGraph?.id,
                            graph: graph.name,
                        };
                    }

                    let api = API_GRAPHER[graph.name];

                    if (
                        graph.name === "static_network" &&
                        mapValueGraphTab[storedData[graph.name].metric] === 1
                    ) {
                        api = ApiService.getDataGraphBetweenness;
                    }

                    try {
                        const resultDataGraph = await api(params, activity.id);
                        dataGraphResolutionAux = {
                            ...dataGraphResolutionAux,
                            [graph.name]: resultDataGraph.data,
                        };
                    } catch (error) {
                        setOpenLoader(false);
                        setAlertText("Ocurrió un error intente nuevamente!");
                        setSeverity("error");
                        setShowAlert(true);
                        setTimeout(() => {
                            setShowAlert(false);
                            setAlertText("");
                            setSeverity("");
                        }, 3000);
                    }
                    graphs.push(graph.name);
                })
            );
            const graphSorted = graphs.sort((a, b) => MAP_GRAPHS[a].localeCompare(MAP_GRAPHS[b]));
            setActualGraphResolution(graphSorted[0]);
            setGraphListAvailableResolution(graphSorted);
            setGraphSelectedMapResolution(storedData);
            setDataGraphResolution(dataGraphResolutionAux);
        }
        setOpenLoader(false);
    };

    const getStudentResolutions = async () => {
        setOpenLoader(true);
        try {
            const result = await ApiService.getStudentActivityResolutions({
                student_activity: id,
            });
            setOpenLoader(false);
            if (result.data.length > 0) {
                const sarSortedList = result.data
                    .map((d) => ({
                        deliveryDate: moment(d.delivery_date).format("DD/MM/YYYY"),
                        partialDeliveries: d.partial_deliveries,
                        id: d.id,
                        feedback: d.feedback,
                        comment: d.comment,
                    }))
                    .sort(
                        (a, b) =>
                            moment(b.deliveryDate, "DD/MM/YYYY") -
                            moment(a.deliveryDate, "DD/MM/YYYY")
                    );

                setConfigurationResolutions(sarSortedList[0]);
                setStudentActivityResolutions(sarSortedList);
                return;
            }
            setValueTab(1);
        } catch {
            setOpenLoader(false);
        }
    };

    const properties = [
        {
            title: "Fuente",
            description: activity?.static_data_source
                ? activity?.static_data_source?.name
                : "Reddit",
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Título actividad",
            description: activity?.title,
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Acciones",
            description: activity?.title,
            show:
                auth?.selectedRole?.name === "teacher" &&
                studentActivityResolutions.length > 0 &&
                valueTab === 0 &&
                actualResolution,
            showModal: false,
            button: true,
            component: (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Button
                        disabled={studentActivityResolutions.length === 0}
                        variant="contained"
                        onClick={() => {
                            setOpenCorrectionModal(true);
                        }}
                    >
                        {[
                            QUALIFICATION.approved,
                            QUALIFICATION.disapproved,
                            QUALIFICATION.reDelivery,
                        ].includes(actualResolution?.feedback?.qualification)
                            ? "Editar Correccion"
                            : "Corregir"}
                    </Button>
                    <Tooltip
                        enterTouchDelay={0}
                        leaveTouchDelay={15000}
                        disableTouchListener={false}
                        title={
                            <Typography variant="body2">
                                Esta acción corresponde a la entrega que se esta visualizando
                                actualmente (Ver Entrega en gris)
                            </Typography>
                        }
                    >
                        <IconButton size="small">
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: "Enunciado",
            description: activity?.statement,
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Título enunciado",
            description: activity?.statement_title,
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Descripción",
            description: activity?.description,
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Objetivo",
            description: activity?.objetive,
            show: true,
            showModal: true,
            button: false,
        },
        {
            title: "Propietario actividad",
            description: activity?.owner?.email,
            show: true,
            showModal: false,
            button: false,
        },
        {
            title: "Fecha",
            description: moment(activity?.datetime_added).format("DD/MM/YYYY"),
            show: true,
            showModal: false,
            button: false,
        },
        {
            title: "Visibilidad",
            description: VISIBILITY_MAP[activity?.visiblity || VISIBILITY.public],
            show: true,
            showModal: false,
            button: false,
        },
    ];

    const graphs = [
        { name: "Tabla de datos", value: "dataTable", dataSource: "static" },
        { name: "Columnas agrupadas", value: "column", dataSource: "static" },
        { name: "Líneas agrupadas", value: "line", dataSource: "static" },
        { name: "Torta", value: "pie", dataSource: "static" },
        { name: "Burbujas", value: "bubble", dataSource: "static" },
        { name: "Dispersión", value: "dispersion", dataSource: "static" },
        { name: "Mapa", value: "map", dataSource: "static" },
        {
            name: "Grafo de interacción entre usuarios",
            value: "social_user_comments",
            dataSource: "dynamic",
        },
        {
            name: "Grafo de coocurrencias de palabras",
            value: "social_word_frequency",
            dataSource: "dynamic",
        },
        { name: "Grafo", value: "static_network", dataSource: "static" },
        { name: "Nube de palabras", value: "word_cloud", dataSource: "static" },
    ];
    const [actualGraph, setActualGraph] = useState("bubble");
    const [actualGraphResolution, setActualGraphResolution] = useState("pie");

    const handleChangeGraphResolution = (event) => {
        setActualGraphResolution(event.target.value);
    };

    const handleChangeTab = (event, newValue) => {
        setValueTab(newValue);
        setDataSource("static");
    };

    const qualify = async (qualification) => {
        const data = {
            qualification: qualification?.status,
            observation: qualification?.observation,
            feedback:
                studentActivityResolutions.length > 1
                    ? actualResolution?.feedback?.id || null
                    : null,
        };
        return await ApiService.qualify(data, id);
    };

    const onDeleteFilterApplied = (obj, criterion) => {
        const criterionMapFilter = {
            include: "filterInclude",
            exclude: "filterExclude",
        };

        const criterionFilter = criterionMapFilter[criterion];
        const [criterionList, setCriterionList] = criterionMapList[criterion];
        const criterionListFiltered = criterionList.filter(
            (crit) => crit.valueChip !== obj.valueChip
        );
        setCriterionList(criterionListFiltered);
        setGraphSelectedMap({
            ...graphSelectedMap,
            [actualGraph]: {
                ...graphSelectedMap[actualGraph],
                [criterionFilter]: criterionListFiltered,
            },
        });
    };

    const getStudentActivity = async () => {
        let result;
        let activity;
        if (idActivity) {
            result = await ApiService.getActivity(idActivity);
            activity = result.data;
        } else {
            result = await ApiService.getStudentActivity(id, {
                role_name: auth?.selectedRole?.name,
            });
            activity = result.data.course_activity.activity;
            setStudentActivity(result.data);
        }

        if (activity?.static_data_source) {
            const headers = activity.static_data_source.headers?.split("|");
            setDatasetColumns(headers);
        }
        const graphSorted = activity.graphs.sort((a, b) =>
            MAP_GRAPHS[a].localeCompare(MAP_GRAPHS[b])
        );
        setGraphListAvailable(graphSorted);
        setActivity(activity);
        setActualGraph(graphSorted[0]);
    };

    useEffect(() => {
        if (id) {
            getStudentActivity();
        }
    }, []);

    useEffect(() => {
        if (valueTab === 0 && !Boolean(idActivity) && activity) {
            getStudentResolutions();
        }
    }, [valueTab, activity]);

    const handleClickShowDeliveryButton = (row) => {
        setDataGraphResolution({
            dataTable: {},
            column: {},
            line: {},
            pie: {},
            bubble: {},
            dispersion: {},
            map: {},
            social_user_comments: {},
            social_word_frequency: {},
            static_network: {},
            word_cloud: {},
        });
        setConfigurationResolutions(row);
    };

    const deliveriesColumns = [
        { field: "deliveryDate", headerName: "Fecha", flex: 0.7 },
        {
            field: "feedback",
            headerName: "Nota",
            flex: 1,
            hideable: true,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        disabled={!params.row.feedback}
                        onClick={() => {
                            setActualFeedbackOpened(params.row);
                            setOpenFeedbackModal(true);
                        }}
                    >
                        Ver Nota
                    </Button>
                );
            },
        },
        {
            field: "actions",
            headerName: "Acciones",
            flex: 1,
            hideable: true,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        disabled={params.row.id === actualResolution.id}
                        onClick={() => {
                            handleClickShowDeliveryButton(params.row);
                        }}
                    >
                        Ver entrega
                    </Button>
                );
            },
        },
    ];

    const saveCorrection = () => {
        if (!correctionStatus.status) {
            setAlertText("Debe elegir una calificación!");
            setSeverity("error");
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
                setAlertText("");
                setSeverity("");
            }, 3000);
            return;
        }

        if (!correctionStatus.observation) {
            setAlertText("El comentario no puede estar vacío!");
            setSeverity("error");
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
                setAlertText("");
                setSeverity("");
            }, 3000);
            return;
        }
        setOpenConfirmationDialog(true);
    };

    const handleSaveCorrection = async () => {
        setOpenConfirmationDialog(false);
        setOpenLoader(true);

        const res = await qualify(correctionStatus);
        if (res.status === 200) {
            setOpenCorrectionModal(false);
            getStudentResolutions();
            setOpenLoader(false);
        }
        setOpenLoader(false);
    };
    const handleOpenTutorial = (type) => {
        if (type === "static") {
            console.log("Tutorial estatico");
        } else if (type === "dynamic") {
            console.log("Tutorial estatico");
        }
        setOpenTutorialModal(true);
    };

    return (
        <Box
            sx={{
                width: "100%",
                pl: { xs: "20px", md: 5 },
                pr: { xs: "20px", md: 10 },
            }}
        >
            <Box style={{ padding: "10px 0px 0px 0px" }}>
                <BackButton />
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={valueTab} onChange={handleChangeTab} aria-label="basic tabs example">
                    <Tab
                        disabled={Boolean(idActivity) || studentActivityResolutions.length === 0}
                        label="Entregas realizadas"
                    />
                    <Tab label="Nueva entrega" />
                </Tabs>
            </Box>

            <div>
                <h3>Información de actividad </h3>
                <Grid container>
                    {properties
                        .filter((p) => p.show)
                        .slice(0, 3)
                        .map((property) =>
                            property.show ? (
                                <Grid item xs={12} md={4} key={property.title}>
                                    <u>
                                        <h4>{property.title}:</h4>
                                    </u>
                                    {!property.button ? (
                                        <h4
                                            style={{
                                                wordWrap: "break-word",
                                                overflow: "hidden",
                                                whiteSpace: "normal",
                                            }}
                                        >
                                            {property.description}
                                        </h4>
                                    ) : (
                                        property.component
                                    )}
                                </Grid>
                            ) : (
                                <></>
                            )
                        )}
                    <GenericDialog
                        title="Información de la actividad"
                        open={showMore}
                        setOpen={setShowMore}
                        onClose={() => setShowMore(false)}
                        saveButton={false}
                        cancelButton={false}
                        mandatoryText={false}
                    >
                        <DialogContent>
                            <Grid container>
                                {properties
                                    .filter((p) => p.showModal)
                                    .map((property) => (
                                        <Grid item xs={12} key={property.title}>
                                            <DialogContentText>
                                                <strong>{property.title}:</strong>{" "}
                                                {property.description}
                                            </DialogContentText>
                                        </Grid>
                                    ))}
                            </Grid>
                        </DialogContent>
                    </GenericDialog>

                    <Grid item xs={12}>
                        <Button
                            variant="text"
                            onClick={() => {
                                setShowMore((prevState) => !prevState);
                            }}
                        >
                            Ver más
                        </Button>
                    </Grid>
                </Grid>
            </div>

            <br />
            <TabPanelComponent value={valueTab} index={0}>
                <TabResolution
                    studentActivityResolutions={studentActivityResolutions}
                    deliveriesColumns={deliveriesColumns}
                    actualGraphResolution={actualGraphResolution}
                    handleChangeGraphResolution={handleChangeGraphResolution}
                    graphListAvailableResolution={graphListAvailableResolution}
                    graphs={graphs}
                    dataGraphResolution={dataGraphResolution}
                    graphSelectedMapResolution={graphSelectedMapResolution}
                    setGraphSelectedMapResolution={setGraphSelectedMapResolution}
                    setDataGraphResolution={setDataGraphResolution}
                    actualResolution={actualResolution}
                    getStudentResolutions={getStudentResolutions}
                    datasetColumns={datasetColumns}
                    studentActivity={studentActivity}
                    getStudentActivity={getStudentActivity}
                    activity={activity}
                />
            </TabPanelComponent>
            <TabPanelComponent value={valueTab} index={1}>
                <TabNewResolution
                    datasetColumns={datasetColumns}
                    graphSelectedMap={graphSelectedMap}
                    setGraphSelectedMap={setGraphSelectedMap}
                    includeChipList={includeChipList}
                    excludeChipList={excludeChipList}
                    setIncludeChipList={setIncludeChipList}
                    setExcludeChipList={setExcludeChipList}
                    studentActivityId={id}
                    graphListAvailable={graphListAvailable}
                    graphs={graphs}
                    studentActivity={studentActivity}
                    actualGraph={actualGraph}
                    getStudentActivity={getStudentActivity}
                    setActualGraph={setActualGraph}
                    handleChangeTabDataSource={(dataSourceType) => {
                        setDataSource(dataSourceType);
                    }}
                    callbackHandleChangeTabPanel={() => {
                        setGraphSelectedMap(DEFAULT_DATA_GRAPHER);
                        handleChangeTab(null, 0);
                    }}
                    handleOpenTutorial={handleOpenTutorial}
                    dataSource={dataSource}
                    mapGraphSelectedMapTab={mapGraphSelectedMapTab}
                    onDeleteFilterApplied={onDeleteFilterApplied}
                    valueTabParent={valueTab}
                    activity={activity}
                />
            </TabPanelComponent>
            {openCorrectionModal && (
                <GenericDialog
                    title="Corrección de la entrega"
                    mandatorytext={false}
                    open={openCorrectionModal}
                    setOpen={setOpenCorrectionModal}
                    handleSave={() => saveCorrection()}
                    onClose={() => {
                        setOpenCorrectionModal(false);
                    }}
                >
                    <DialogContent>
                        <Grid container gap={3}>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormLabel id="demo-row-radio-buttons-group-label">
                                        Calificación
                                    </FormLabel>
                                    <RadioGroup
                                        aria-required
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="row-radio-buttons-group"
                                        value={correctionStatus.status}
                                        onChange={(evt) =>
                                            setCorrectionStatus({
                                                ...correctionStatus,
                                                status: evt.target.value,
                                            })
                                        }
                                    >
                                        <FormControlLabel
                                            value={QUALIFICATION.approved}
                                            control={<Radio />}
                                            label="Aprobado"
                                        />
                                        <FormControlLabel
                                            value={QUALIFICATION.disapproved}
                                            control={<Radio />}
                                            label="Desaprobado"
                                        />
                                        <FormControlLabel
                                            value={QUALIFICATION.reDelivery}
                                            control={<Radio />}
                                            label="Reentrega"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="outlined-multiline-static"
                                    label="Comentarios de entrega"
                                    multiline
                                    minRows={7}
                                    maxRows={10}
                                    placeholder="Escriba un comentario.."
                                    value={correctionStatus.observation}
                                    onChange={(evt) => {
                                        setCorrectionStatus({
                                            ...correctionStatus,
                                            observation: evt.target.value,
                                        });
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                </GenericDialog>
            )}

            {openFeedbackModal && (
                <GenericDialog
                    title="Corrección de la entrega"
                    mandatorytext={false}
                    open={openFeedbackModal}
                    setOpen={setOpenFeedbackModal}
                    cancelButton={null}
                    saveButton={null}
                >
                    <DialogContent>
                        <Grid container gap={3}>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormLabel id="demo-row-radio-buttons-group-label">
                                        Calificación
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="row-radio-buttons-group"
                                        value={actualFeedbackOpened?.feedback?.qualification}
                                    >
                                        <FormControlLabel
                                            value={QUALIFICATION.approved}
                                            control={<Radio />}
                                            label="Aprobado"
                                            disabled
                                        />
                                        <FormControlLabel
                                            value={QUALIFICATION.disapproved}
                                            control={<Radio />}
                                            label="Desaprobado"
                                            disabled
                                        />
                                        <FormControlLabel
                                            value={QUALIFICATION.reDelivery}
                                            control={<Radio />}
                                            label="Reentrega"
                                            disabled
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="outlined-multiline-static"
                                    label="Comentarios de entrega"
                                    multiline
                                    minRows={7}
                                    maxRows={10}
                                    placeholder="Escriba un comentario.."
                                    disabled
                                    value={actualFeedbackOpened?.feedback?.observation}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                </GenericDialog>
            )}

            {openTutorialModal && (
                <GenericDialog
                    title="Tutorial de graficador"
                    open={openTutorialModal}
                    setOpen={setOpenTutorialModal}
                    saveButton={false}
                    cancelButton={false}
                    mandatoryText={false}
                >
                    <ImageSlider
                        images={[
                            "learn-more1.png",
                            "learn-more2.png",
                            "learn-more3.png",
                            "learn-more4.png",
                        ]}
                    ></ImageSlider>
                </GenericDialog>
            )}
            {openConfirmationDialog && (
                <GenericDialog
                    handleSave={() => handleSaveCorrection()}
                    open={openConfirmationDialog}
                    setOpen={setOpenConfirmationDialog}
                    title={"Confirmación"}
                    mandatoryText={false}
                >
                    <Typography variant="p" px={3}>
                        ¿Está seguro que desea guardar esta información?
                    </Typography>
                </GenericDialog>
            )}
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: 999999999,
                }}
                open={openLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {showAlert && (
                <GenericAlert
                    severity={severity}
                    text={alertText}
                    style={{
                        position: "fixed",
                        zIndex: "9999999999999999",
                        top: "50%",
                        left: "30%",
                    }}
                />
            )}
        </Box>
    );
};

export default Grapher;
